import { Meta } from 'types/common';
import { CaseListItem, TextVisitServiceType } from 'types/textVisit';
import { capitalize } from 'lodash';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

export interface TimeDisplayResult {
    text: string;
    color: string;
}

/**
 * 计算时间差并返回显示文本和颜色
 * @param date 目标日期
 * @returns 包含显示文本和颜色的对象
 */
export function getTimeAgo(date: string | Date): TimeDisplayResult {
    const now = dayjs();
    const targetDate = dayjs.utc(date).local();
    const diffMinutes = now.diff(targetDate, 'minutes');
    const diffHours = now.diff(targetDate, 'hours');
    const diffDays = now.diff(targetDate, 'days');

    // < 10 min -> Just now (default color)
    if (diffMinutes < 10) {
        return {
            text: 'Just now',
            color: '#F3F4F6',
        };
    }

    // 10 - 1 hour -> x mins ago (warning color)
    if (diffMinutes < 60) {
        return {
            text: `${diffMinutes} mins ago`,
            color: '#FEF3C7',
        };
    }

    // Today (>1 hour) -> x hours ago (default color)
    if (diffHours < 24) {
        return {
            text: `${diffHours} hours ago`,
            color: '#FEF2F2',
        };
    }

    // 1-4 days -> x days ago (warning color)
    if (diffDays >= 1 && diffDays <= 4) {
        return {
            text: `${diffDays} days ago`,
            color: '#FEE2E2',
        };
    }

    // >4 days -> x days ago (danger color)
    if (diffDays > 4) {
        return {
            text: `${diffDays} days ago`,
            color: '#FECACA',
        };
    }

    return {
        text: `${diffDays} days ago`,
        color: '#F3F4F6',
    };
}

// 计算现在和传入时间的间隔，从15天开始倒数，计算剩余时间
export function getTimeLeft(date: string | Date, gap: number = 15): TimeDisplayResult {
    const now = dayjs();
    const targetDate = dayjs.utc(date).local();

    // 计算从目标日期开始经过的天数
    const diffDays = now.diff(targetDate, 'days');

    // 计算剩余天数（从gap开始倒数）
    const remainingDays = gap - diffDays;

    if (remainingDays <= 0) {
        return {
            text: 'Expired',
            color: '#FECACA',
        };
    }

    if (remainingDays > 10) {
        return {
            text: `${remainingDays} days left`,
            color: '#F3F4F6',
        };
    }
    if (remainingDays > 4) {
        return {
            text: `${remainingDays} days left`,
            color: '#FEF2F2',
        };
    }

    if (remainingDays >= 1) {
        return {
            text: `${remainingDays} days left`,
            color: '#FEE2E2',
        };
    }

    const diffHours = now.diff(targetDate, 'hours');
    const diffMinutes = now.diff(targetDate, 'minutes');
    const remainingHours = 24 - diffHours;
    const remainingMinutes = 60 - diffMinutes;

    return {
        text: `${remainingHours}h ${remainingMinutes}m left`,
        color: '#FECACA',
    };
}

export function getAgeByBirthday(birthday: string | Date | null | undefined): number {
    if (!birthday) return 0;
    const birthDate = dayjs.utc(birthday).local();
    if (Number.isNaN(birthDate.valueOf())) return 0;

    const today = dayjs();
    let age = today.diff(birthDate, 'year');
    const monthDiff = today.diff(birthDate, 'month');
    const dayDiff = today.diff(birthDate, 'day');

    if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
        age -= 1;
    }
    return Math.max(0, age);
}

export const getDetailText = (
    caseData: Pick<CaseListItem, 'state' | 'patientGender' | 'patientBirthday'>,
    stateList: Meta[],
) => {
    const age = caseData.patientBirthday ? getAgeByBirthday(caseData.patientBirthday) : undefined;
    const parts = [
        stateList?.find((state) => state.value === caseData.state)?.label || caseData.state || undefined,
        caseData.patientGender ? capitalize(caseData.patientGender) : undefined,
        age !== undefined ? String(age) : undefined,
    ].filter((v): v is string => Boolean(v && String(v).length));
    return parts.join(', ');
};

export const getRefillServiceTitle = ({
    serviceType,
    specialty,
    assessmentName,
}: {
    serviceType?: TextVisitServiceType | string | null;
    specialty?: string | null;
    assessmentName?: string | null;
}): string | undefined => {
    if (
        serviceType !== TextVisitServiceType.MEDICATION_REFILL &&
        serviceType !== TextVisitServiceType.PRESCRIPTION_REFILL
    ) {
        return undefined;
    }

    const base = specialty || assessmentName || '';
    const suffix = serviceType === TextVisitServiceType.MEDICATION_REFILL ? 'medication refill' : 'prescription refill';

    if (!base) {
        return suffix;
    }

    return `${base} ${suffix}`;
};
